import { Button, TextField } from "@mui/material";

import { TagColorKind, TagView } from "generated/graphql";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { useMemo, useState } from "react";
import { Navigate } from "react-router";
import { useAppSelector } from "redux/hooks";
import { selectLensEditorLens, selectLensEditorTags, upsertTag } from "redux/slices/lensEditor";
import { RowSelectionState, createColumnHelper } from "@tanstack/react-table";
import { PTable } from "components/Table/PTable";
import { TagName } from "components/tags/TagName";

import { TagDescription } from "./TagDescription";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { TagFields } from "./TagFields";
import { TagPreview } from "./TagPreview";
import PublishTagsModal from "../PublishTagsModal";
const slugify = (s: string) => s.toLowerCase().replace(/\s+/g, "_");

type MaybeNewTag = TagView & { isNewTag?: boolean };

function createNewTag(existing: TagView[]): MaybeNewTag {
  const newCount = existing.filter((t) => t.systemName.startsWith("new_tag")).length;
  return {
    isNewTag: true,
    systemName: `new_tag_${newCount}`,
    displayName: "New Tag",
    color: TagColorKind.Blue,
    colorValue: "#4080ee",
    description: null,
    fields: [],
    readOnlyFields: [],
    supportOnlyFields: [],
    filteredByDefaultFields: [],
  };
}
const columnHelper = createColumnHelper<TagView>();

export function Tags() {
  const serverTags = useAppSelector(selectLensEditorTags);
  const [tags, setTags] = useState(serverTags);
  const { lensId } = useParamNumbers(["lensId"]);
  const lens = useAppSelector(selectLensEditorLens);
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState<RowSelectionState>({});
  const [showPublishModal, setShowPublishModal] = useState(false);
  const snackpatch = useAppDispatchWithSnackbar();
  // const confirmCtx = useContext(ConfirmationDialogContext);
  const doUpsert = async (tag: MaybeNewTag) => {
    const payload = { ...tag, systemName: tag.isNewTag ? slugify(tag.displayName) : tag.systemName, lensId: lens.id };
    setTags((tags) => tags.map((t) => (t.systemName === tag.systemName ? payload : t)));
    try {
      await snackpatch(upsertTag(payload), {
        successMessage: `Updated tag: ${tag.displayName}`,
        errorMessage: "Failed to update tag",
      });
    } catch (err) {
      // if there's an error, undo our optimistic update
      setTags((tags) => tags.map((t) => (t.systemName === payload.systemName ? tag : t)));
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("displayName", {
        header: "Name",
        cell: (info) => <TagName {...info} onSave={doUpsert} />,
        // w-48
        size: 192,
        minSize: 192,
      }),
      columnHelper.accessor("systemName", {
        header: "System Name",
        cell: (info) => <span className="text-gray-100 italic">{info.getValue()}</span>,
        size: 132,
        minSize: 132,
      }),
      columnHelper.accessor("description", {
        header: "Description",
        cell: TagDescription,
        size: 300,
        minSize: 200,
        meta: {
          onSave: doUpsert,
        },
      }),
      columnHelper.display({ header: "Preview", cell: TagPreview, minSize: 200 }),
      columnHelper.accessor("fields", {
        header: "Fields",
        cell: TagFields,
        size: 300,
        minSize: 200,
      }),
    ],
    [snackpatch]
  );

  if (lensId === undefined) {
    return <Navigate to=".." />;
  }

  // const deleteTag = (systemName: string) => {
  //   confirmCtx.confirm({
  //     title: "Delete Tag",
  //     prompt: (
  //       <div className="flex flex-col gap-4">
  //         <Alert severity="warning">
  //           This will remove the tags for <i>{systemName}</i> on <strong>every response</strong>
  //         </Alert>
  //         <p className="text-sm font-extrabold">Delete Tag `${systemName}`?</p>
  //       </div>
  //     ),
  //     callback: async () => {
  //       return runGqlMutation(DeleteTagDocument, {
  //         lensId: lensId,
  //         systemName: systemName,
  //       })
  //         .then(() => dispatch(fetchTags({ lensId })))
  //         .then(() => {
  //           enqueueSnackbar(`Successfully deleted '${systemName}'`, {
  //             variant: "success",
  //           });
  //         })
  //         .catch(() =>
  //           enqueueSnackbar(`Failed to delete '${systemName}'`, {
  //             variant: "error",
  //           })
  //         );
  //     },
  //   });
  // };

  const addTag = () => {
    setTags((t) => [...t, createNewTag(t)]);
  };

  return (
    <div className="bg-white rounded-md shadow p-4">
      <div className="flex items-center justify-between pb-2">
        <div>
          <TextField label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          {lens.isFamilyPrimary && (
            <Button variant="outlined" onClick={() => setShowPublishModal(true)}>
              Publish to Lens Family
            </Button>
          )}
          <Button variant="contained" onClick={addTag}>
            Add Tag
          </Button>
        </div>
      </div>
      <div className="max-w-full">
        <PTable
          data={tags}
          columns={columns}
          state={{ rowSelection: selectedTags, globalFilter: search }}
          globalFilterFn="includesString"
          onRowSelectionChange={setSelectedTags}
          getRowId={(row, _) => row.systemName}
        />
      </div>
      {showPublishModal && <PublishTagsModal open={true} close={() => setShowPublishModal(false)} lensId={lensId} />}
    </div>
  );
}
